// Email Pipeline Panel - Email classification & monitor decision stats
//
// Displays email classification accuracy, category breakdown, pipeline status,
// and monitor decision stats (notify/unsure/ignore).

import { useEffect, useState, useCallback } from 'react';
import { emailMonitorDecisionService } from '../../services/emailMonitorDecisions';
import type {
  DecisionStatsResponse,
  EmailClassificationStats,
  PipelineStatus,
} from '../../services/emailMonitorDecisions';
import { formatMoney } from '../../utils/formatting';
import styles from './StatisticsTab.module.css';

export default function EmailPipelinePanel() {
  const [classStats, setClassStats] = useState<EmailClassificationStats | null>(null);
  const [decisionStats, setDecisionStats] = useState<DecisionStatsResponse | null>(null);
  const [pipelineStatus, setPipelineStatus] = useState<PipelineStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.allSettled([
        emailMonitorDecisionService.getClassificationStats(),
        emailMonitorDecisionService.getStats(),
        emailMonitorDecisionService.getPipelineStatus(),
      ]);

      if (results[0].status === 'fulfilled') setClassStats(results[0].value);
      if (results[1].status === 'fulfilled') setDecisionStats(results[1].value);
      if (results[2].status === 'fulfilled') setPipelineStatus(results[2].value);

      const failures = results.filter((r) => r.status === 'rejected');
      if (failures.length === results.length) {
        setError('Failed to load email pipeline data');
      } else if (failures.length > 0) {
        setError(`Some data could not be loaded (${failures.length} of ${results.length} failed)`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load email pipeline data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) return <div className={styles.loading}>Loading email pipeline data...</div>;

  return (
    <>
      {error && <div className={styles.error}>{error}</div>}

      {/* Classification KPIs */}
      {classStats && (
        <>
          <div className={styles.kpiGrid}>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Total Classified</span>
              <span className={styles.kpiValue}>{classStats.total_classified.toLocaleString()}</span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Unclassified</span>
              <span className={styles.kpiValue}>{classStats.total_unclassified.toLocaleString()}</span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Accuracy</span>
              <span className={styles.kpiValue}>
                {classStats.accuracy != null ? `${classStats.accuracy.toFixed(1)}%` : 'N/A'}
              </span>
              <span className={styles.kpiChange}>
                {classStats.human_verified_count} verified
              </span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Avg Confidence</span>
              <span className={styles.kpiValue}>{classStats.avg_confidence.toFixed(1)}%</span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>High Confidence</span>
              <span className={styles.kpiValue} style={{ color: '#16a34a' }}>
                {classStats.high_confidence_count.toLocaleString()}
              </span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Low Confidence</span>
              <span className={styles.kpiValue} style={{ color: '#dc2626' }}>
                {classStats.low_confidence_count.toLocaleString()}
              </span>
            </div>
          </div>

          {/* Category Breakdown */}
          {Object.keys(classStats.by_category).length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Classification by Category</div>
              <CategoryBar categories={classStats.by_category} total={classStats.total_classified} />
            </div>
          )}

          {/* Processing Status */}
          {Object.keys(classStats.processing_status).length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Processing Status</div>
              <div className={styles.statusGrid}>
                {Object.entries(classStats.processing_status).map(([status, count]) => (
                  <div key={status} className={styles.statusItem}>
                    <div className={styles.statusItemValue}>{count.toLocaleString()}</div>
                    <div className={styles.statusItemLabel}>{status.replace(/_/g, ' ')}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Monitor Decision Stats */}
      {decisionStats && (
        <>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Monitor Decisions</div>
            <div className={styles.kpiGrid}>
              <div className={styles.kpiCard}>
                <span className={styles.kpiLabel}>Total Decisions</span>
                <span className={styles.kpiValue}>{decisionStats.total_decisions.toLocaleString()}</span>
              </div>
              <div className={styles.kpiCard}>
                <span className={styles.kpiLabel}>Notify</span>
                <span className={styles.kpiValue} style={{ color: '#16a34a' }}>
                  {decisionStats.notify_count.toLocaleString()}
                </span>
              </div>
              <div className={styles.kpiCard}>
                <span className={styles.kpiLabel}>Unsure</span>
                <span className={styles.kpiValue} style={{ color: '#f59e0b' }}>
                  {decisionStats.unsure_count.toLocaleString()}
                </span>
              </div>
              <div className={styles.kpiCard}>
                <span className={styles.kpiLabel}>Ignore</span>
                <span className={styles.kpiValue} style={{ color: '#94a3b8' }}>
                  {decisionStats.ignore_count.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <div className={styles.kpiGrid}>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Avg Confidence</span>
              <span className={styles.kpiValue}>
                {decisionStats.avg_confidence != null
                  ? `${(decisionStats.avg_confidence * 100).toFixed(1)}%`
                  : 'N/A'}
              </span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Avg Latency</span>
              <span className={styles.kpiValue}>
                {decisionStats.avg_latency_ms != null
                  ? `${(decisionStats.avg_latency_ms / 1000).toFixed(1)}s`
                  : 'N/A'}
              </span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Total Cost</span>
              <span className={styles.kpiValue}>{formatMoney(decisionStats.total_cost_usd)}</span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Feedback Processed</span>
              <span className={styles.kpiValue}>{decisionStats.feedback_processed_count.toLocaleString()}</span>
            </div>
          </div>
        </>
      )}

      {/* Pipeline Status */}
      {pipelineStatus && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Pipeline Status</div>
          <div className={styles.kpiGrid}>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Scheduler</span>
              <span
                className={styles.kpiValue}
                style={{ color: pipelineStatus.scheduler_enabled ? '#16a34a' : '#dc2626' }}
              >
                {pipelineStatus.scheduler_enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Queue</span>
              <span className={styles.kpiValue}>
                {pipelineStatus.queue_size} / {pipelineStatus.queue_capacity}
              </span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Never Processed</span>
              <span className={styles.kpiValue}>{pipelineStatus.never_processed_count.toLocaleString()}</span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Errors</span>
              <span
                className={styles.kpiValue}
                style={pipelineStatus.error_count > 0 ? { color: '#dc2626' } : undefined}
              >
                {pipelineStatus.error_count}
              </span>
            </div>
            {pipelineStatus.dead_letter_count > 0 && (
              <div className={styles.kpiCard}>
                <span className={styles.kpiLabel}>Dead Letter</span>
                <span className={styles.kpiValue} style={{ color: '#dc2626' }}>
                  {pipelineStatus.dead_letter_count}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function CategoryBar({ categories, total }: { categories: Record<string, number>; total: number }) {
  const sorted = Object.entries(categories).sort(([, a], [, b]) => b - a);
  const maxCount = Math.max(...sorted.map(([, c]) => c), 1);

  return (
    <div className={styles.barChart}>
      {sorted.map(([category, count]) => (
        <div key={category} className={styles.barRow}>
          <span className={styles.barLabel} style={{ width: 100 }}>{category}</span>
          <div className={styles.barTrack}>
            <div
              className={`${styles.barFill} ${styles.trendBarFill}`}
              style={{ width: `${(count / maxCount) * 100}%` }}
            />
          </div>
          <span className={styles.barValue} style={{ width: 100 }}>
            {count.toLocaleString()} ({total > 0 ? ((count / total) * 100).toFixed(1) : 0}%)
          </span>
        </div>
      ))}
    </div>
  );
}
